import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { faqItems } from "@/mocks/faq";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.gaoxiaochigua.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "常见问题",
          item: "https://www.gaoxiaochigua.cn/faq",
        },
      ],
    },
  ],
};

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(0);
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return faqItems;
    return faqItems.filter(
      (item) =>
        item.question.toLowerCase().includes(kw) || item.answer.toLowerCase().includes(kw),
    );
  }, [keyword]);

  return (
    <SiteLayout>
      <PageMeta
        title="常见问题 - 高校吃瓜网 | 内容来源、学校筛选与发言规范"
        description="关于高校吃瓜网的常见问题解答：内容如何更新、怎样找到自己学校的吃瓜现场、社区发言规范以及投稿合作方式，一次说清楚。"
        keywords="高校吃瓜常见问题,高校吃瓜社区规则,高校吃瓜论坛怎么用,校园话题投稿"
        path="/faq"
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </PageMeta>

      <div className="mx-auto w-full px-4 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12">
        <Breadcrumbs items={[{ label: "常见问题" }]} />

        <header className="mt-5 max-w-3xl">
          <h1 className="font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[44px]">
            常见问题
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
            这里整理了大家最常问到的六个问题，涵盖内容来源、学校筛选、发言规范与投稿合作。没找到答案的话，欢迎直接给我们留言。
          </p>
        </header>

        <div className="mt-7 flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
          <div className="w-full lg:max-w-3xl">
            <div className="flex h-11 items-center gap-2.5 rounded-full border border-background-300 bg-background-100 px-5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索问题关键词…"
                aria-label="搜索常见问题"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>

            {filtered.length > 0 ? (
              <div className="mt-5 flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {filtered.map((item, index) => {
                  const open = openIndex === index;
                  return (
                    <article key={item.question}>
                      <h2>
                        <button
                          type="button"
                          onClick={() => setOpenIndex(open ? -1 : index)}
                          aria-expanded={open}
                          className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-100"
                        >
                          <span className="font-heading text-base font-bold text-foreground-950">
                            {item.question}
                          </span>
                          <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                              open
                                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                                : "bg-background-200 text-foreground-600"
                            }`}
                          >
                            <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
                          </span>
                        </button>
                      </h2>
                      {open && (
                        <p className="px-5 pb-5 text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                          {item.answer}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-question-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有匹配的问题
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或者直接到关于我们页面留言。
                </p>
              </div>
            )}
          </div>

          <aside className="w-full lg:max-w-sm">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-customer-service-2-line text-xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                还是没有找到答案？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                把问题留在联系页面，我们会补充进这个列表，也可以直接发送邮件到 contact@gaoxiaochigua.cn。
              </p>
              <Link
                to="/about#contact"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
              >
                去留言
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关页面</h2>
              <ul className="mt-3.5 flex flex-col gap-2.5">
                <li>
                  <Link to="/topics" className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    浏览全部讨论区
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    了解我们的内容原则
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}